import { Radio } from 'lucide-react';

/**
 * 到達性（オンライン人数）のサマリーカード
 *
 * **既存の「アクティブユーザー」とは別の指標である。** あちらは当月に言語モデルの
 * 利用実績があった人数、こちらは**いま到達可能な**人数。名称を共有しないこと。
 *
 * 人数は利用者（アカウント）単位で数える。同じ人が複数端末で起動していても 1 人。
 * 到達性の記録がそもそも利用者ごとに 1 件しかないため、二重に数える余地はない。
 */

export interface PresenceSummaryCardProps {
  /** 現在オンラインと判定された利用者数 */
  onlineCount: number;
  /** 到達性の記録がある利用者数。取得前・取得失敗時は undefined */
  trackedUserCount: number | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function PresenceSummaryCard({
  onlineCount,
  trackedUserCount,
  isLoading,
  isError,
}: PresenceSummaryCardProps) {
  // **取得できていない状態を 0 人と見せない。** 0 人は「依頼先がいない」という
  // 断定であり、取得に失敗しているだけの状態に付けてよい意味ではない
  const unavailable = isError || isLoading || trackedUserCount === undefined;

  /**
   * 記録が 1 件も無い状態。
   *
   * **「誰も起動していない」ではない。** KeepAlive がサーバーへ 1 件も届いて
   * いないことを意味し、配布が行き渡っていない・経路が未配備・認証が通っていない、
   * のいずれかを疑う合図になる。送信側のログは利用者の端末にあって運用者からは
   * 見えないため、この区別をここで出さないと機能の停止に気付く手段が無い。
   */
  const noRecords = !unavailable && trackedUserCount === 0;

  return (
    <div
      className="bg-card rounded-xl p-6 border border-border"
      data-testid="presence-summary-card"
    >
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-lg bg-teal-500/20 text-teal-400">
          <Radio className="w-6 h-6" />
        </div>
        <div>
          <p className="text-muted-foreground text-sm">オンライン中のアプリ</p>
          {unavailable ? (
            <p className="text-lg font-medium text-yellow-400">
              {isLoading ? '取得中...' : '取得できませんでした'}
            </p>
          ) : (
            <p className="text-2xl font-bold text-foreground">
              <span data-testid="presence-online-count">{onlineCount}</span>
              <span className="text-base font-normal text-muted-foreground"> 人</span>
            </p>
          )}
          {!unavailable && (
            <p className="text-xs text-muted-foreground mt-1">
              記録のある利用者 <span data-testid="presence-tracked-count">{trackedUserCount}</span>{' '}
              人
            </p>
          )}
        </div>
      </div>
      {noRecords && (
        <p className="mt-3 text-xs text-yellow-400" data-testid="presence-summary-note">
          到達性の記録が 1
          件もありません。アプリからの申告が届いていない可能性があります（配布状況・API
          の配備・認証の設定を確認してください）。
        </p>
      )}
    </div>
  );
}
